import "@/styles/components/styled-form.css";

const setupDependentDropdown = (select) => {
  const sourceName = select.dataset.dependentSource;

  if (!sourceName || !select.form) {
    return;
  }

  let optionsByValue = {};

  try {
    optionsByValue = JSON.parse(select.dataset.dependentOptions || "{}");
  } catch {
    return;
  }

  const source = select.form.elements[sourceName];

  if (!source) {
    return;
  }

  const placeholderText = select.dataset.dependentPlaceholder || "";

  const update = () => {
    const options = optionsByValue[source.value] || [];
    const previousValue = select.value;

    select.replaceChildren();

    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = placeholderText;
    placeholder.setAttribute("data-placeholder", "true");
    select.appendChild(placeholder);

    options.forEach((option) => {
      const value = typeof option === "string" ? option : option.value;
      const label = typeof option === "string" ? option : option.label;
      const optionEl = document.createElement("option");
      optionEl.value = value;
      optionEl.textContent = label;
      select.appendChild(optionEl);
    });

    const valueStillAvailable = options.some(
      (option) => (typeof option === "string" ? option : option.value) === previousValue,
    );

    select.value = valueStillAvailable ? previousValue : "";
    select.disabled = options.length === 0;
  };

  update();

  source.addEventListener("change", update);
};

export const setupDropdowns = async (form) => {
  const dropdowns = form.querySelectorAll(".js-dropdown, .userform .field.dropdown");

  if (!dropdowns.length) {
    return;
  }

  const { default: easydropdown } = await import("easydropdown-a11y");
  import("@/styles/components/dropdown.css");

  dropdowns.forEach((el) => {
    const select = el.querySelector("select");

    if (!select) {
      return;
    }

    if (select.closest(".edd-root")) {
      return;
    }

    const placeholder = el.querySelector('option[value=""]');

    if (placeholder && !placeholder.hasAttribute("data-placeholder")) {
      placeholder.setAttribute("data-placeholder", true);

      if (!placeholder.textContent) {
        placeholder.textContent = "Select One";
      }
    }

    const opts = {
      behavior: {
        openOnFocus: true,
        maxVisibleItems: 8,
        liveUpdates: true,
        useNativeUiOnMobile: false,
      },
    };

    easydropdown(select, opts);

    setupDependentDropdown(select);
  });
};

const setupMasks = async (form) => {
  const fields = form.querySelectorAll('input[type="tel"], input[data-mask-currency], input[data-mask-num-range]');

  if (!fields.length) {
    return;
  }

  const { default: IMask } = await import("imask/esm/imask");
  await import("imask/esm/masked/number");
  await import("imask/esm/masked/pattern");

  fields.forEach((field) => {
    if (field.type === "tel") {
      IMask(field, {
        mask: "(000) 000-0000",
      });
    }

    if (field.dataset.maskCurrency !== undefined) {
      const mask = IMask(field, {
        mask: Number,
        scale: 0,
        thousandsSeparator: ",",
        min: 1,
      });

      field.addEventListener("updateValue", (e) => {
        field.value = e.detail;
        mask.updateValue();
        field.dispatchEvent(new Event("blur"));
      });
    }

    if (field.dataset.maskNumRange !== undefined) {
      const mask = IMask(field, {
        mask: /^(\d+)?(-)?(\d+)?$/,
      });

      field.addEventListener("updateValue", (e) => {
        field.value = e.detail;
        mask.updateValue();
        field.dispatchEvent(new Event("blur"));
      });
    }
  });
};

const setupSpecialFields = (form) => {
  form.querySelectorAll("input[type=file]").forEach((input) => {
    const placeholder = document.createElement("div");
    placeholder.classList.add("file-input-placeholder");

    if (input.value) {
      placeholder.textContent = input.files[0].name;
    } else {
      placeholder.textContent = input.dataset.placeholder;
    }

    input.parentNode.insertBefore(placeholder, input);

    placeholder.addEventListener("click", () => input.click());

    input.addEventListener("change", (e) => {
      placeholder.textContent = e.target.files[0].name;
      placeholder.classList.add("filled");
    });
  });
};

export const setupStyledForm = (form) => {
  setupDropdowns(form);
  setupMasks(form);
  setupSpecialFields(form);
};
